import { utils, } from '@neo-one/client-common';
import { utils as commonUtils } from '@neo-one/utils';
import { AsyncIterableX } from '@reactivex/ix-esnext-esm/asynciterable/asynciterablex';
import { flatMap } from '@reactivex/ix-esnext-esm/asynciterable/pipe/flatmap';
import { flatten } from '@reactivex/ix-esnext-esm/asynciterable/pipe/flatten';
import { map } from '@reactivex/ix-esnext-esm/asynciterable/pipe/map';
import BigNumber from 'bignumber.js';
import _ from 'lodash';
import { AsyncBlockIterator } from '../AsyncBlockIterator';
import { MissingTransactionDataError } from '../errors';
import { convertCallReceipt, convertConfirmedTransaction, convertInputs, convertInvocationData, convertNetworkSettings, convertOutput, convertRelayTransactionResult, convertStorageItem, } from './convert';
import { DataProviderBase } from './DataProviderBase';
export class NEOONEDataProvider extends DataProviderBase {
    async getClaimAmount(input) {
        return this.capture(async () => this.mutableClient.getClaimAmount({ txid: input.hash, vout: input.index }), 'neo_get_claim_amount');
    }
    async getInvocationData(hash) {
        const [invocationData, transaction] = await Promise.all([
            this.mutableClient.getInvocationData(hash),
            this.mutableClient.getTransaction(hash),
        ]);
        if (transaction.data === undefined) {
            throw new MissingTransactionDataError(hash);
        }
        return convertInvocationData(invocationData, transaction.data.blockHash, transaction.data.blockIndex, hash, transaction.data.transactionIndex);
    }
    iterBlocks(options = {}) {
        return AsyncIterableX.from(new AsyncBlockIterator({
            client: this,
            options,
            fetchTimeoutMS: this.iterBlocksFetchTimeoutMS,
            batchSize: this.iterBlocksBatchSize,
        }));
    }
    async getNetworkSettings() {
        const settings = await this.mutableClient.getNetworkSettings();
        return convertNetworkSettings(settings);
    }
    iterActionsRaw(options = {}) {
        return AsyncIterableX.from(this.iterBlocks(options)).pipe(flatMap(async (block) => {
            const actions = _.flatten(block.transactions.map((transaction) => {
                if (transaction.type === 'InvocationTransaction') {
                    return [...transaction.invocationData.actions];
                }
                return [];
            }));
            return AsyncIterableX.of(...actions);
        }));
    }
    async runConsensusNow() {
        return this.mutableClient.runConsensusNow();
    }
    async updateSettings(options) {
        return this.mutableClient.updateSettings(options);
    }
    async getSettings() {
        return this.mutableClient.getSettings();
    }
    async fastForwardOffset(seconds) {
        return this.mutableClient.fastForwardOffset(seconds);
    }
    async fastForwardToTime(seconds) {
        return this.mutableClient.fastForwardToTime(seconds);
    }
    async reset() {
        return this.mutableClient.reset();
    }
    async getNEOTrackerURL() {
        return this.mutableClient.getNEOTrackerURL();
    }
    async resetProject() {
        return this.mutableClient.resetProject();
    }
    iterStorage(address) {
        return AsyncIterableX.from(this.mutableClient.getAllStorage(address).then((res) => AsyncIterableX.from(res))).pipe(flatten(), map(convertStorageItem));
    }
    async executeGetOutput(input) {
        const output = await this.mutableClient.getOutput({
            txid: input.hash,
            vout: input.index,
        });
        return convertOutput(output);
    }
    async executeTestInvoke(transaction) {
        const receipt = await this.mutableClient.testInvocation(transaction.serializeWire().toString('hex'));
        return convertCallReceipt(receipt);
    }
    async executeRelayTransaction(transaction, _networkFee) {
        const result = await this.mutableClient.relayTransaction(transaction.serializeWire().toString('hex'));
        return convertRelayTransactionResult(result);
    }
    async executeGetTransactionReceipt(hash, options) {
        const result = await this.mutableClient.getTransactionReceipt(hash, options);
        return { ...result, globalIndex: new BigNumber(result.globalIndex) };
    }
    async executeGetUnspentOutputs(address) {
        const account = await this.getAccountInternal(address);
        const outputs = await Promise.all(account.unspent.map(async (input) => {
            const outputJSON = await this.mutableClient.getUnspentOutput(input);
            if (outputJSON === undefined) {
                return undefined;
            }
            const output = convertOutput(outputJSON);
            return {
                asset: output.asset,
                value: output.value,
                address: output.address,
                hash: input.txid,
                index: input.vout,
            };
        }));
        return outputs.filter(commonUtils.notNull);
    }
    async executeGetUnclaimed(address) {
        const account = await this.getAccountInternal(address);
        const amounts = await Promise.all(account.unclaimed.map(async (input) => this.mutableClient.getClaimAmount(input)));
        return {
            unclaimed: convertInputs(account.unclaimed),
            amount: amounts.reduce((acc, amount) => acc.plus(amount), utils.ZERO_BIG_NUMBER),
        };
    }
    async executeGetAccountInternal(address) {
        return this.mutableClient.getAccount(address);
    }
    convertConfirmedTransaction(transaction) {
        return convertConfirmedTransaction(transaction);
    }
}

//# sourceMappingURL=NEOONEDataProvider.js.map
